import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "@/utils/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Clock, AlertTriangle, ArrowRight } from "lucide-react";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import PublicBrandHeader from "@/components/PublicBrandHeader";

export default function PaymentSuccessPage() {
  useDocumentTitle("Payment success", "Confirming your payment with Stripe.");
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | pending | error
  const [info, setInfo] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let mounted = true;
    let timer;

    const poll = async () => {
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        if (!mounted) return;
        setInfo(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          // Learners don't have CRM access — bounce them to their inbox after a short delay
          if (params.get("autoclose")) {
            const email = data.metadata?.member_email || "";
            const domain = email.split("@")[1] || "";
            const provider = domain.toLowerCase();
            const url = provider.includes("gmail") ? "https://mail.google.com" :
                       provider.includes("outlook") || provider.includes("hotmail") || provider.includes("live") ? "https://outlook.live.com/mail" :
                       provider.includes("yahoo") ? "https://mail.yahoo.com" :
                       provider ? `https://${domain}` : null;
            setTimeout(() => { if (url) window.location.href = url; else window.close(); }, 4000);
          }
          return;
        }
        if (data.status === "expired") { setStatus("error"); return; }
        if (attemptsRef.current >= 6) { setStatus("pending"); return; }
        timer = setTimeout(poll, 2500);
      } catch {
        if (!mounted) return;
        if (attemptsRef.current >= 6) { setStatus("error"); return; }
        timer = setTimeout(poll, 2500);
      }
    };
    poll();

    return () => { mounted = false; if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6 bg-slate-50" data-testid="payment-success-page">
      <div className="max-w-md w-full">
        <PublicBrandHeader subtitle="Secure payment" />
        <div className="bg-white rounded-lg border border-border p-8 text-center">
        {status === "checking" && (
          <>
            <Clock size={42} className="mx-auto text-slate-400 animate-pulse mb-3" />
            <h1 className="text-xl font-heading font-bold mb-2">Confirming payment...</h1>
            <p className="text-sm text-muted-foreground">Please wait while we verify your payment with Stripe.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 size={48} className="mx-auto text-emerald-600 mb-3" />
            <h1 className="text-2xl font-heading font-bold mb-2 text-emerald-700">Payment received</h1>
            <p className="text-sm text-muted-foreground mb-6">
              Thank you. Your invoice has been marked as paid and a receipt is now available in the CRM.
            </p>
            {info && (
              <div className="text-xs text-muted-foreground bg-slate-50 rounded p-3 mb-6">
                Total: <span className="font-mono font-semibold">£{(info.amount_total / 100).toFixed(2)}</span>
                {" · "}Currency: <span className="uppercase">{info.currency}</span>
              </div>
            )}
            <Button onClick={() => navigate("/payments")} className="w-full" data-testid="back-to-payments-btn">
              Back to Payments <ArrowRight size={14} className="ml-2" />
            </Button>
          </>
        )}
        {status === "pending" && (
          <>
            <Clock size={42} className="mx-auto text-amber-600 mb-3" />
            <h1 className="text-xl font-heading font-bold mb-2">Still processing</h1>
            <p className="text-sm text-muted-foreground mb-6">
              Your payment is taking a moment. We'll automatically update the invoice once Stripe confirms — you can safely close this page.
            </p>
            <Link to="/payments"><Button variant="outline" className="w-full">Back to Payments</Button></Link>
          </>
        )}
        {status === "error" && (
          <>
            <AlertTriangle size={42} className="mx-auto text-red-600 mb-3" />
            <h1 className="text-xl font-heading font-bold mb-2">Couldn't verify payment</h1>
            <p className="text-sm text-muted-foreground mb-6">
              We were unable to confirm the payment status. Check the Payments page or contact support.
            </p>
            <Link to="/payments"><Button variant="outline" className="w-full">Back to Payments</Button></Link>
          </>
        )}
        </div>
      </div>
    </div>
  );
}
